import React, { useEffect, useState } from 'react';
import {
  CheckCircle2,
  AlertCircle,
  Info,
  AlertTriangle,
  X,
} from 'lucide-react';

export interface ToastData {
  id?: string;
  title?: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
}

interface ToastNotificationProps {
  toast: ToastData | null;
  onClose: () => void;
}

export default function ToastNotification({ toast, onClose }: ToastNotificationProps) {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (!toast) return;

    setProgress(100);
    const duration = toast.duration || 4500;
    const intervalTime = 50;
    const step = (intervalTime / duration) * 100;

    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prev - step;
      });
    }, intervalTime);

    return () => clearInterval(timer);
  }, [toast]);

  if (!toast) return null;

  const type = toast.type || 'success';
  const defaultTitle =
    toast.title ||
    (type === 'success'
      ? 'Perubahan Berhasil Disimpan!'
      : type === 'error'
      ? 'Terjadi Kesalahan Sistem'
      : type === 'warning'
      ? 'Peringatan Sistem'
      : 'Pemberitahuan Informasi');

  const getStyleConfig = () => {
    switch (type) {
      case 'success':
        return {
          wrapperBg: 'bg-slate-900/95 border-emerald-500/60 shadow-emerald-950/40',
          iconBg: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
          titleColor: 'text-emerald-300',
          textColor: 'text-slate-200',
          barColor: 'bg-emerald-500',
          badgeText: 'SUKSES',
          badgeBg: 'bg-emerald-950 text-emerald-300 border-emerald-800',
          icon: <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 animate-bounce" />,
        };
      case 'error':
        return {
          wrapperBg: 'bg-slate-950/95 border-rose-600/70 shadow-rose-950/50',
          iconBg: 'bg-rose-500/20 text-rose-400 border border-rose-500/30',
          titleColor: 'text-rose-300',
          textColor: 'text-rose-100',
          barColor: 'bg-rose-500',
          badgeText: 'GAGAL',
          badgeBg: 'bg-rose-950 text-rose-300 border-rose-800',
          icon: <AlertCircle className="w-5 h-5 text-rose-400 shrink-0" />,
        };
      case 'warning':
        return {
          wrapperBg: 'bg-slate-900/95 border-amber-500/60 shadow-amber-950/40',
          iconBg: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
          titleColor: 'text-amber-300',
          textColor: 'text-slate-200',
          barColor: 'bg-amber-500',
          badgeText: 'PERHATIAN',
          badgeBg: 'bg-amber-950 text-amber-300 border-amber-800',
          icon: <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />,
        };
      case 'info':
      default:
        return {
          wrapperBg: 'bg-slate-900/95 border-sky-500/60 shadow-sky-950/40',
          iconBg: 'bg-sky-500/20 text-sky-400 border border-sky-500/30',
          titleColor: 'text-sky-300',
          textColor: 'text-slate-200',
          barColor: 'bg-sky-500',
          badgeText: 'INFO',
          badgeBg: 'bg-sky-950 text-sky-300 border-sky-800',
          icon: <Info className="w-5 h-5 text-sky-400 shrink-0" />,
        };
    }
  };

  const style = getStyleConfig();

  return (
    <div
      id="global-toast-notification-banner"
      role="alert"
      className="fixed top-5 right-4 sm:right-6 z-[99999] max-w-md w-[calc(100vw-2rem)] sm:w-auto animate-in slide-in-from-top-4 fade-in duration-300 ease-out"
    >
      <div
        className={`relative overflow-hidden rounded-2xl border backdrop-blur-md shadow-2xl p-4 sm:p-4.5 flex flex-col gap-2.5 transition-all ${style.wrapperBg}`}
      >
        {/* Main Header & Body */}
        <div className="flex items-start gap-3.5">
          {/* Status Icon */}
          <div className={`p-2.5 rounded-xl shrink-0 flex items-center justify-center ${style.iconBg}`}>
            {style.icon}
          </div>

          {/* Text Content */}
          <div className="flex-1 pr-6 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md border ${style.badgeBg}`}
              >
                {style.badgeText}
              </span>
              <h4 className={`text-sm font-extrabold tracking-tight ${style.titleColor}`}>
                {defaultTitle}
              </h4>
            </div>

            <p className={`text-xs font-medium leading-relaxed ${style.textColor}`}>
              {toast.message}
            </p>
          </div>

          {/* Close button */}
          <button
            id="close-toast-notification-btn"
            onClick={onClose}
            className="absolute top-3.5 right-3.5 p-1 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
            title="Tutup Notifikasi"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Dynamic Progress / Auto-Dismiss Bar */}
        <div className="w-full bg-slate-800/80 h-1 rounded-full overflow-hidden mt-1">
          <div
            className={`h-full transition-all duration-75 ease-linear ${style.barColor}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
}
